import { z } from 'zod';
import { dayDatesInclusive, nightDatesBetween } from './dates.js';
import { ALIAS_RE } from './alias.js';

export const DateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');

// CHF 10'000 in Rappen — generous cap, prevents pathological inputs.
export const MAX_RAPPEN_PER_FIELD = 1_000_000;

export const StayNightInput = z.object({
  date: DateString,
  members_adult: z.number().int().min(0),
  members_child: z.number().int().min(0),
  guests_adult: z.number().int().min(0),
  guests_child: z.number().int().min(0),
});

export type StayNightInput = z.infer<typeof StayNightInput>;

// Plain object shape — extendable by admin schemas. The cross-field rules
// (date coverage, sum ≥ 1) live in `applyStayRules` so both schemas reuse them.
export const StayBaseShape = z.object({
  check_in: DateString,
  check_out: DateString,
  nights: z.array(StayNightInput).min(1),
  stayover_rappen: z.number().int().min(0).max(MAX_RAPPEN_PER_FIELD),
  beverages_rappen: z.number().int().min(0).max(MAX_RAPPEN_PER_FIELD),
  notes: z.string().max(2000).nullable().optional(),
  pay_twint: z.boolean().optional(),
  pay_slip: z.boolean().optional(),
  pay_cash: z.boolean().optional(),
  // External-guest contact block. Required on `ExternalStayInput`; left
  // optional/nullable here so the existing roster (`StayInput`) and admin
  // schemas keep accepting payloads without these fields.
  surname: z.string().max(120).nullable().optional(),
  street: z.string().max(200).nullable().optional(),
  zipcode: z.string().max(20).nullable().optional(),
  town: z.string().max(120).nullable().optional(),
  email: z.string().email().max(200).nullable().optional(),
});

type StayBase = z.infer<typeof StayBaseShape>;

function applyStayRules(v: StayBase, ctx: z.RefinementCtx): void {
  if (v.check_in >= v.check_out) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['check_out'],
      message: 'check_in must be before check_out',
    });
    return;
  }

  const expected = nightDatesBetween(v.check_in, v.check_out);
  const have = new Set(v.nights.map((n) => n.date));
  if (v.nights.length !== expected.length || have.size !== expected.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['nights'],
      message: 'nights must contain exactly one row per day in the stay',
    });
    return;
  }
  for (const d of expected) {
    if (!have.has(d)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['nights'],
        message: `missing night for ${d}`,
      });
      return;
    }
  }

  // Zero-person stays are intentionally allowed — e.g. a day-only visit
  // recorded for the CHF totals (beverages) with no overnight headcount.
}

export const StayInput = StayBaseShape.superRefine(applyStayRules);
export type StayInput = z.infer<typeof StayInput>;

// Admin edit payload: same shape plus an optional override flag that skips
// the server-side capacity guard (for historical corrections). The `paid`
// flag toggles `stays.paid_at` (true → stamp NOW if not already paid;
// false → clear; omitted → leave untouched).
export const AdminStayUpdate = StayBaseShape.extend({
  override_capacity: z.boolean().optional(),
  paid: z.boolean().optional(),
}).superRefine(applyStayRules);
export type AdminStayUpdate = z.infer<typeof AdminStayUpdate>;

// Admin create payload: same as update but also carries the `name` (which
// the guest path takes from the session). Server stamps `edited_at` on
// insert so manually-added bookings are visually distinct from
// guest-submitted ones in the admin views. `paid=true` also stamps
// `paid_at` on creation.
export const AdminStayCreate = StayBaseShape.extend({
  name: z.string().min(1).max(120),
  override_capacity: z.boolean().optional(),
  paid: z.boolean().optional(),
}).superRefine(applyStayRules);
export type AdminStayCreate = z.infer<typeof AdminStayCreate>;

export const GuestLoginInput = z.object({
  name: z.string().min(1).max(120),
  password: z.string().min(1).max(200),
});

// External-guest entry payload. The new form gates submission on six
// non-optional identity fields. `name` here is a first name (Vorname);
// `surname` is the family name. This split is the only place where the
// existing `stays.name` column's meaning is overloaded — see migration
// 0008 and the journal for context.
export const ExternalStayInput = StayBaseShape.extend({
  name: z.string().min(1).max(120),
  surname: z.string().min(1).max(120),
  street: z.string().min(1).max(200),
  zipcode: z.string().min(1).max(20),
  town: z.string().min(1).max(120),
  email: z.string().email().max(200),
}).superRefine(applyStayRules);
export type ExternalStayInput = z.infer<typeof ExternalStayInput>;

export const ExternalLoginInput = z.object({
  password: z.string().min(1).max(200),
});

export const AdminLoginInput = z.object({
  username: z.string().min(1).max(120),
  password: z.string().min(1).max(200),
});

export const AvailabilityRangeQuery = z.object({
  from: DateString,
  to: DateString,
});

export const AvailabilityUpdate = z.object({
  total_beds: z.number().int().min(0),
  note: z.string().max(500).nullable().optional(),
});

export const AvailabilityBulkUpdate = z.object({
  from: DateString,
  to: DateString,
  total_beds: z.number().int().min(0),
  note: z.string().max(500).nullable().optional(),
});

// ─── Hut Duty (names + scheduled slots) ─────────────────────────────────

export const HutDutySlotInput = z
  .object({
    check_in: DateString,
    check_out: DateString,
    note: z.string().max(500).nullable().optional(),
    // Beds reserved on each night the slot covers, in date order. Empty
    // array → no beds reserved on any night (back-compat for legacy
    // callers that don't send the field). A non-empty array must have
    // exactly `nightDatesBetween(check_in, check_out).length` entries —
    // enforced in the .superRefine below.
    beds_per_night: z.array(z.number().int().min(0).max(1000)).optional().default([]),
    // Duty marker per calendar day (inclusive both ends). True = "Hütte
    // Bewartet" badge on the public calendar for that date. Empty array
    // → no marker on any day (back-compat for legacy callers). A
    // non-empty array must have exactly
    // `dayDatesInclusive(check_in, check_out).length` entries =
    // `nightDatesBetween(check_in, check_out).length + 1`.
    is_duty_per_day: z.array(z.boolean()).optional().default([]),
  })
  .superRefine((v, ctx) => {
    if (v.check_in >= v.check_out) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['check_out'],
        message: 'check_in must be before check_out',
      });
      return;
    }
    if (v.beds_per_night.length > 0) {
      const expected = nightDatesBetween(v.check_in, v.check_out).length;
      if (v.beds_per_night.length !== expected) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['beds_per_night'],
          message: `beds_per_night must contain ${expected} entries`,
        });
      }
    }
    if (v.is_duty_per_day.length > 0) {
      const expected = dayDatesInclusive(v.check_in, v.check_out).length;
      if (v.is_duty_per_day.length !== expected) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['is_duty_per_day'],
          message: `is_duty_per_day must contain ${expected} entries`,
        });
      }
    }
  });
export type HutDutySlotInput = z.infer<typeof HutDutySlotInput>;

/** Optional memorable login alias accepted alongside the real name on
 * create / patch. The server auto-generates one when omitted so every row
 * always ends up with an alias. */
export const LoginAlias = z.string().regex(ALIAS_RE, 'Expected word-NN').nullable().optional();

/** Single-add path: rule #2 — a slot is required when creating a hut duty entry. */
export const HutDutyInput = z.object({
  name: z.string().min(1).max(120),
  note: z.string().max(500).nullable().optional(),
  login_alias: LoginAlias,
  slot: HutDutySlotInput,
});
export type HutDutyInput = z.infer<typeof HutDutyInput>;

/**
 * Bulk-paste path. Each entry is a name plus an optional initial slot.
 * The textarea in the admin UI accepts "Name" or "Name YYYY-MM-DD YYYY-MM-DD"
 * per line and translates to this shape client-side. Existing rows whose name
 * is already in the roster are silently skipped server-side (counted as
 * `skipped` in the response).
 */
export const HutDutyBulkInput = z.object({
  entries: z
    .array(
      z.object({
        name: z.string().min(1).max(120),
        slot: HutDutySlotInput.optional(),
      }),
    )
    .min(1)
    .max(200),
});

export const HutDutyPatch = z.object({
  name: z.string().min(1).max(120).optional(),
  note: z.string().max(500).nullable().optional(),
  active: z.boolean().optional(),
  login_alias: LoginAlias,
  email: z.string().email().max(200).nullable().optional(),
});

// Settings rotations (admin-only).
export const PasswordRotation = z.object({
  password: z.string().min(8).max(200),
});

export const HutCapacityUpdate = z.object({
  capacity: z.number().int().min(0).max(1000),
});

// Per-category nightly prices, stored as integer Rappen. Used to auto-fill
// the Stayover amount on the guest form.
export const PricesUpdate = z.object({
  members_adult: z.number().int().min(0).max(MAX_RAPPEN_PER_FIELD),
  members_child: z.number().int().min(0).max(MAX_RAPPEN_PER_FIELD),
  guests_adult: z.number().int().min(0).max(MAX_RAPPEN_PER_FIELD),
  guests_child: z.number().int().min(0).max(MAX_RAPPEN_PER_FIELD),
});
export type PricesUpdate = z.infer<typeof PricesUpdate>;

export const DEFAULT_PRICES: PricesUpdate = {
  members_adult: 1500,
  members_child: 700,
  guests_adult: 2500,
  guests_child: 1000,
};

// ─── Hut-Duty Poll & negotiation ────────────────────────────────────────

export const PollStatus = z.enum(['draft', 'open', 'closed', 'finalized']);
export type PollStatus = z.infer<typeof PollStatus>;

export const PollSlotState = z.enum(['open', 'locked', 'closed']);
export type PollSlotState = z.infer<typeof PollSlotState>;

/** Shared check_in<check_out + per-night/per-day array-length rules, reused by
 * the poll slot schema (mirrors the inline rules on `HutDutySlotInput`). */
function applyDutySlotArrayRules(
  v: { check_in: string; check_out: string; beds_per_night: number[]; is_duty_per_day: boolean[] },
  ctx: z.RefinementCtx,
): void {
  if (v.check_in >= v.check_out) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['check_out'],
      message: 'check_in must be before check_out',
    });
    return;
  }
  if (v.beds_per_night.length > 0) {
    const expected = nightDatesBetween(v.check_in, v.check_out).length;
    if (v.beds_per_night.length !== expected) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['beds_per_night'],
        message: `beds_per_night must contain ${expected} entries`,
      });
    }
  }
  if (v.is_duty_per_day.length > 0) {
    const expected = dayDatesInclusive(v.check_in, v.check_out).length;
    if (v.is_duty_per_day.length !== expected) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['is_duty_per_day'],
        message: `is_duty_per_day must contain ${expected} entries`,
      });
    }
  }
}

export const PollCreate = z.object({
  title: z.string().min(1).max(200),
  deadline: DateString.nullable().optional(),
  intro_text: z.string().max(5000).nullable().optional(),
});

export const PollPatch = z.object({
  title: z.string().min(1).max(200).optional(),
  status: PollStatus.optional(),
  deadline: DateString.nullable().optional(),
  intro_text: z.string().max(5000).nullable().optional(),
});

// Full slot replace (create + edit share this shape, like HutDutySlotInput).
export const PollSlotInput = z
  .object({
    check_in: DateString,
    check_out: DateString,
    label: z.string().max(200).nullable().optional(),
    state: PollSlotState.optional().default('open'),
    assignee_name_id: z.number().int().positive().nullable().optional(),
    beds_per_night: z.array(z.number().int().min(0).max(1000)).optional().default([]),
    is_duty_per_day: z.array(z.boolean()).optional().default([]),
  })
  .superRefine(applyDutySlotArrayRules);
export type PollSlotInput = z.infer<typeof PollSlotInput>;

// Bulk slot insert — the admin UI generates a series (e.g. every weekend in a
// range) client-side and posts the resulting slots together.
export const PollSlotBulkInput = z.object({
  slots: z.array(PollSlotInput).min(1).max(200),
});

// Admin invite: link to a roster entry (name_id) and/or a free display name.
export const PollInviteInput = z.object({
  name_id: z.number().int().positive().nullable().optional(),
  display_name: z.string().max(120).nullable().optional(),
  email: z.string().email().max(200).nullable().optional(),
});

// "Add a new email that isn't in the roster yet" — creates/looks up a roster
// entry by name, sets its email, then invites it to the poll.
export const PollInviteEmailInput = z.object({
  name: z.string().min(1).max(120),
  email: z.string().email().max(200),
});

// Send (or re-send) invitation emails for a poll.
export const PollSendInput = z.object({
  resend: z.boolean().optional().default(false),
});

export const PollFinalizeInput = z.object({
  // Also copy each winner's self-chosen display_name onto allowed_names.name.
  sync_names: z.boolean().optional().default(false),
  // Write availability=0 overrides for closed (event) slots.
  write_closed_availability: z.boolean().optional().default(false),
});

// Participant-facing payloads.
export const PollNameInput = z.object({
  display_name: z.string().min(1).max(120),
});

export const PollClaimInput = z.object({
  slot_id: z.number().int().positive(),
  rank: z.number().int().min(1).max(99).optional().default(1),
  // How many beds the participant needs for this slot. Flows into the
  // imported hut-duty slot's per-night beds on finalize.
  beds: z.number().int().min(0).max(1000).optional().default(0),
});

export const PollClaimRank = z.object({
  rank: z.number().int().min(1).max(99),
});

// Participant changes the bed count on a claim they already hold.
export const PollClaimBeds = z.object({
  beds: z.number().int().min(0).max(1000),
});

// Admin-side claim: the admin puts a participant on an open slot before the
// poll runs. Identical in storage to a claim the participant makes themselves,
// so the participant sees it as their own and others can still contest it.
// Unlike PollClaimInput this carries participant_id, and beds is nullable --
// the route fills null from the slot's own beds_per_night[0] rather than
// defaulting to 0, because a 0-bed claim imports with no bed rows at all.
export const PollAdminClaimInput = z.object({
  slot_id: z.number().int().positive(),
  participant_id: z.number().int().positive(),
  rank: z.number().int().min(1).max(99).optional().default(1),
  beds: z.number().int().min(0).max(1000).nullable().optional(),
});
export type PollAdminClaimInput = z.infer<typeof PollAdminClaimInput>;

export const PollCommentInput = z.object({
  body: z.string().min(1).max(2000),
});
